import { useState, useEffect } from "react";
import { API, useAuth } from "../App";
import axios from "axios";
import { Users, Building2, Globe, ChevronDown, X, Search } from "lucide-react";

/**
 * VisibilitySelector - Component to select who can see a course/exam
 * Props:
 *  - visibilidad: "todos" | "departamentos" | "usuarios"
 *  - departamentos_permitidos: string[]
 *  - usuarios_permitidos: string[]
 *  - onChange: (field, value) => void
 *  - helperEndpoint: "/capacitaciones" or "/examenes" (for fetching users/depts)
 */
export default function VisibilitySelector({ visibilidad, departamentos_permitidos, usuarios_permitidos, onChange, helperEndpoint }) {
  const { getAuthHeaders } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded || visibilidad === "todos") return;
    const fetch = async () => {
      try {
        const [uRes, dRes] = await Promise.all([
          axios.get(`${API}${helperEndpoint}/helpers/usuarios`, getAuthHeaders()),
          axios.get(`${API}${helperEndpoint}/helpers/departamentos`, getAuthHeaders())
        ]);
        setUsuarios(uRes.data);
        setDepartamentos(dRes.data);
        setLoaded(true);
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [visibilidad, loaded, getAuthHeaders, helperEndpoint]);

  const filteredUsers = searchUser
    ? usuarios.filter(u => (u.name || "").toLowerCase().includes(searchUser.toLowerCase()) || (u.email || "").toLowerCase().includes(searchUser.toLowerCase()))
    : usuarios;

  const toggleDept = (dName) => {
    const current = departamentos_permitidos || [];
    const updated = current.includes(dName) ? current.filter(d => d !== dName) : [...current, dName];
    onChange("departamentos_permitidos", updated);
  };

  const toggleUser = (userId) => {
    const current = usuarios_permitidos || [];
    const updated = current.includes(userId) ? current.filter(u => u !== userId) : [...current, userId];
    onChange("usuarios_permitidos", updated);
  };

  return (
    <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3" data-testid="visibility-selector">
      <label className="block text-sm font-medium mb-1">Visibilidad - ¿Quién puede ver esto?</label>
      <div className="flex gap-2">
        {[
          { id: "todos", label: "Todos", icon: Globe, desc: "Visible para todos los usuarios" },
          { id: "departamentos", label: "Por Departamento", icon: Building2, desc: "Solo departamentos seleccionados" },
          { id: "usuarios", label: "Usuarios Específicos", icon: Users, desc: "Solo usuarios seleccionados" },
        ].map(opt => (
          <button key={opt.id} type="button"
            onClick={() => onChange("visibilidad", opt.id)}
            className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${
              visibilidad === opt.id ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
            }`}>
            <opt.icon className={`w-5 h-5 mb-1 ${visibilidad === opt.id ? "text-blue-600" : "text-gray-400"}`} />
            <p className={`text-sm font-medium ${visibilidad === opt.id ? "text-blue-700" : "text-gray-700 dark:text-gray-300"}`}>{opt.label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
          </button>
        ))}
      </div>

      {visibilidad === "departamentos" && (
        <div>
          <p className="text-sm text-gray-500 mb-2">Seleccione los departamentos que tendrán acceso:</p>
          {departamentos.length === 0 ? <p className="text-xs text-gray-400">Cargando departamentos...</p> : (
            <div className="flex flex-wrap gap-2">
              {departamentos.map(d => {
                const name = d.name || d.nombre || "";
                const selected = (departamentos_permitidos || []).includes(name);
                return (
                  <button key={d.id || name} type="button" onClick={() => toggleDept(name)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      selected ? "bg-blue-100 border-blue-400 text-blue-700 font-medium" : "bg-gray-50 border-gray-200 text-gray-600 hover:border-blue-300 dark:bg-gray-700 dark:border-gray-600"
                    }`}>
                    {name} {selected && <X className="w-3 h-3 inline ml-1" />}
                  </button>
                );
              })}
            </div>
          )}
          {(departamentos_permitidos || []).length > 0 && (
            <p className="text-xs text-blue-600 mt-2">{departamentos_permitidos.length} departamento(s) seleccionado(s)</p>
          )}
        </div>
      )}

      {visibilidad === "usuarios" && (
        <div>
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600"
              placeholder="Buscar usuario por nombre o email..." value={searchUser} onChange={e => setSearchUser(e.target.value)} />
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {filteredUsers.map(u => {
              const selected = (usuarios_permitidos || []).includes(u.id);
              return (
                <label key={u.id} className={`flex items-center gap-3 p-2 rounded cursor-pointer ${selected ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-gray-50 dark:hover:bg-gray-700"}`}>
                  <input type="checkbox" checked={selected} onChange={() => toggleUser(u.id)} />
                  <div>
                    <p className="text-sm font-medium">{u.name}</p>
                    <p className="text-xs text-gray-500">{u.email} {u.department ? `- ${u.department}` : ""}</p>
                  </div>
                </label>
              );
            })}
          </div>
          {(usuarios_permitidos || []).length > 0 && (
            <p className="text-xs text-blue-600 mt-2">{usuarios_permitidos.length} usuario(s) seleccionado(s)</p>
          )}
        </div>
      )}
    </div>
  );
}
